import { Badge, EmptyState } from '@paguz/ui';
import { useQuery } from '@tanstack/react-query';
import { HistoryIcon } from 'lucide-react';
import { api, queryKeys } from '../../api/endpoints.ts';
import { DataList, ErrorState, LoadingRows, PageHeader, Panel } from '../../components/page.tsx';
import { appConfig } from '../../config.ts';
import { formatDateTime } from '../../lib/format.ts';
import { ROLE_LABELS } from '../../lib/rbac.ts';
import { useSession } from '../../lib/session.ts';
import { AuditTable } from '../audit/audit-table.tsx';

/** Auditoría visible de la propia sesión (T-CON-01): lo que hice desde que entré. */
export function ActivityPage() {
  const session = useSession();
  const userId = session?.user.id ?? '';
  const query = useQuery({
    queryKey: queryKeys.audit({ actorId: userId }),
    queryFn: () => api.listAudit({ actorId: userId }),
    enabled: userId !== '',
  });
  if (!session) return null;
  const sinceLogin = (query.data?.data ?? []).filter((e) => e.at >= session.loginAt.slice(0, 19));

  return (
    <>
      <PageHeader
        title="Mi actividad"
        description="Tu sesión y cada acción que registraste en la auditoría. Toda acción sensible queda con motivo."
      />
      <div className="grid gap-6 xl:grid-cols-[22rem_1fr]">
        <Panel title="Sesión actual">
          <DataList
            columns={1}
            items={[
              { label: 'Usuario', value: `${session.user.fullName} · ${session.user.email}` },
              {
                label: 'Roles',
                value: (
                  <span className="flex flex-wrap gap-1">
                    {session.user.roles.map((r) => (
                      <Badge key={r} variant="secondary">
                        {ROLE_LABELS[r]}
                      </Badge>
                    ))}
                  </span>
                ),
              },
              {
                label: 'Tenant',
                value: `${appConfig.tenant.displayName} (${appConfig.tenant.code})`,
              },
              { label: 'Inicio de sesión', value: formatDateTime(session.loginAt) },
              {
                label: 'Vence',
                value: formatDateTime(session.expiresAt),
                hint: 'Token de consola de 8 horas.',
              },
            ]}
          />
        </Panel>
        <Panel
          title="Acciones registradas"
          description={`${sinceLogin.length} en esta sesión · ${query.data?.data.length ?? 0} en total`}
          bodyClassName="p-0"
        >
          {query.isPending ? (
            <div className="p-5">
              <LoadingRows rows={4} />
            </div>
          ) : query.isError ? (
            <div className="p-5">
              <ErrorState error={query.error} onRetry={() => void query.refetch()} />
            </div>
          ) : query.data.data.length === 0 ? (
            <div className="p-5">
              <EmptyState
                icon={<HistoryIcon />}
                title="Aún no hay acciones"
                description="Cuando cambies algo, aparecerá aquí."
              />
            </div>
          ) : (
            <AuditTable entries={query.data.data} />
          )}
        </Panel>
      </div>
    </>
  );
}
